/**
 * Two-step sign-in (board PB-62, plan docs/plan/implementation/two-factor-sign-in.md).
 *
 * Firebase (with Identity Platform) offers two second steps: an authenticator
 * app (TOTP) and a text message (SMS). Both are turned on per project; UAT
 * since 2026-10-27, texts limited to US and Canada numbers. A user may add
 * both, so losing one device isn't a lock-out.
 *
 * Adding a step needs a recent sign-in (Firebase answers
 * `auth/requires-recent-login`), so the UI re-checks the password first.
 * Signing in, Firebase answers `auth/multi-factor-auth-required`; the
 * resolver here finishes it with the code.
 */

import {
  getMultiFactorResolver,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type TotpSecret,
  type User,
  type UserCredential,
} from 'firebase/auth'
import { auth } from '../firebase'
import { UserRoles } from '@/constants/userRoles'

/** Window event fired after a step is added or removed, so the gate rechecks. */
export const TWO_STEP_CHANGED = 'mntnd:two-step-changed'

/**
 * Where to get the authenticator apps named on the Security tab. Google's
 * goes straight to the store for the phone in hand (user, 2026-10-27);
 * on a computer it goes to Google's help page, which links both stores.
 */
const GOOGLE_AUTHENTICATOR_URLS = {
  ios: 'https://apps.apple.com/us/app/google-authenticator/id388497605',
  android:
    'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
  other: 'https://support.google.com/accounts/answer/1066447',
}
export const MICROSOFT_AUTHENTICATOR_URL =
  'https://www.microsoft.com/en-us/security/mobile-authenticator-app'

export function googleAuthenticatorUrl(): string {
  const ua = navigator.userAgent
  // iPadOS reports itself as a Mac; a touch screen gives it away
  const ios =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  if (ios) return GOOGLE_AUTHENTICATOR_URLS.ios
  if (/Android/.test(ua)) return GOOGLE_AUTHENTICATOR_URLS.android
  return GOOGLE_AUTHENTICATOR_URLS.other
}

export const TOTP = TotpMultiFactorGenerator.FACTOR_ID // 'totp'
export const SMS = PhoneMultiFactorGenerator.FACTOR_ID // 'phone'

/** True when a Firebase error means "now ask for the second step". */
export function isSecondStepRequired(
  error: unknown
): error is MultiFactorError {
  return (
    (error as { code?: string })?.code === 'auth/multi-factor-auth-required'
  )
}

export function enrolledSteps(user: User | null): MultiFactorInfo[] {
  return user ? multiFactor(user).enrolledFactors : []
}

/** An invisible reCAPTCHA for sending texts, bound to a page element (or its id). */
export function smsVerifier(
  container: string | HTMLElement
): RecaptchaVerifier {
  return new RecaptchaVerifier(auth, container, { size: 'invisible' })
}

// ---------------------------------------------------------------------------
// Adding a step
// ---------------------------------------------------------------------------

export interface AppSetup {
  secret: TotpSecret
  /** otpauth:// address for the QR code. */
  qrUrl: string
  /** The key to type in when the QR can't be scanned. */
  key: string
}

export async function startAppSetup(user: User): Promise<AppSetup> {
  const session = await multiFactor(user).getSession()
  const secret = await TotpMultiFactorGenerator.generateSecret(session)
  return {
    secret,
    qrUrl: secret.generateQrCodeUrl(user.email ?? 'MNTND user', 'MNTND'),
    key: secret.secretKey,
  }
}

export async function finishAppSetup(
  user: User,
  setup: AppSetup,
  code: string
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    setup.secret,
    code
  )
  await multiFactor(user).enroll(assertion, 'Authenticator app')
}

/** Sends a code to `phoneNumber` (E.164, e.g. +16475550100); returns its id. */
export async function startTextSetup(
  user: User,
  phoneNumber: string,
  verifier: RecaptchaVerifier
): Promise<string> {
  const session = await multiFactor(user).getSession()
  return new PhoneAuthProvider(auth).verifyPhoneNumber(
    { phoneNumber, session },
    verifier
  )
}

export async function finishTextSetup(
  user: User,
  verificationId: string,
  code: string,
  label: string
): Promise<void> {
  const credential = PhoneAuthProvider.credential(verificationId, code)
  await multiFactor(user).enroll(
    PhoneMultiFactorGenerator.assertion(credential),
    label
  )
}

export async function removeStep(user: User, factorUid: string) {
  await multiFactor(user).unenroll(factorUid)
}

// ---------------------------------------------------------------------------
// Signing in (or re-checking) with a second step
// ---------------------------------------------------------------------------

export function secondStepResolver(
  error: MultiFactorError
): MultiFactorResolver {
  return getMultiFactorResolver(auth, error)
}

export async function sendSignInText(
  resolver: MultiFactorResolver,
  hint: MultiFactorInfo,
  verifier: RecaptchaVerifier
): Promise<string> {
  return new PhoneAuthProvider(auth).verifyPhoneNumber(
    { multiFactorHint: hint, session: resolver.session },
    verifier
  )
}

export async function finishWithApp(
  resolver: MultiFactorResolver,
  hint: MultiFactorInfo,
  code: string
): Promise<UserCredential> {
  return resolver.resolveSignIn(
    TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code)
  )
}

export async function finishWithText(
  resolver: MultiFactorResolver,
  verificationId: string,
  code: string
): Promise<UserCredential> {
  return resolver.resolveSignIn(
    PhoneMultiFactorGenerator.assertion(
      PhoneAuthProvider.credential(verificationId, code)
    )
  )
}

/** A short label for a step: "Authenticator app" / "Text to •••• 0100". */
export function stepLabel(info: MultiFactorInfo): string {
  if (info.factorId === SMS) {
    const phone = (info as MultiFactorInfo & { phoneNumber?: string })
      .phoneNumber
    return `Text to •••• ${phone?.slice(-4) ?? ''}`
  }
  return info.displayName || 'Authenticator app'
}

/** Who a sign-in stopped at the second step is (PB-81): Firebase's account
 *  id, plus the email when it gives one. For Google the email is the Google
 *  account's, which can differ from the MNTND sign-in email. */
export function secondStepAccount(error: MultiFactorError): {
  uid?: string
  email?: string
} {
  const data = error.customData as {
    email?: string
    _serverResponse?: { localId?: string; email?: string }
  }
  return {
    uid: data?._serverResponse?.localId || undefined,
    email: data?.email || data?._serverResponse?.email || undefined,
  }
}

// ---------------------------------------------------------------------------
// Admins confirm a phone as they join (PB-88, D126/D129)
// ---------------------------------------------------------------------------

/** The page where an admin confirms their phone as the second step. */
export const CONFIRM_PHONE_PATH = '/confirm-phone'

/** Roles that must have a second step before using the app. */
export function phoneStepRequired(role?: string | null): boolean {
  return role === UserRoles.ADMIN
}

/** Where to go once an invitation is accepted. */
export function afterAcceptPath(role?: string | null): string {
  return phoneStepRequired(role) ? CONFIRM_PHONE_PATH : '/dashboard'
}
